import styled from "@emotion/styled";
import { FC, useState } from "react";
import { Field, FieldRenderProps } from "react-final-form";

import { FormField, Input, SpacingToken, Switch, Text } from "@livechat/design-system-react-components";

import { Divider } from "components/divider/Divider";
import Flex from "components/layout/Flex";
import { IFormBlockSubmit } from "store/entities/contact-forms/interfaces";
import { withVar } from "themes/helpers";

import { SUBMIT_ERROR_MESSAGE, SUBMIT_SUCCESS_MESSAGE } from "../constants";

interface IProps {
  name: string;
}

export const SubmitBlock: FC<IProps> = ({ name }) => {
  return <Field name={name} component={SubmitBlockInput} />;
};

const SubmitBlockInput: FC<FieldRenderProps<IFormBlockSubmit>> = ({ input, meta }) => {
  return (
    <Flex column>
      <Field name={`${input.name}.value`}>
        {({ input: contentInput, meta: contentMeta }) => (
          <FormField labelText="Send button content" error={contentMeta.error}>
            <Input {...contentInput} error={contentMeta.error} />
          </FormField>
        )}
      </Field>
      <Flex column gap={withVar(SpacingToken.Spacing2)}>
        <Divider />
        <Field name={`${input.name}.wide`} type="checkbox">
          {({ input: wideInput }) => (
            <Flex alignItems="center" gap={withVar(SpacingToken.Spacing2)}>
              <Switch
                size="medium"
                on={wideInput.checked}
                onChange={wideInput.onChange}
                data-testid="wide-button-toggle"
              />
              <Text>Wide button design</Text>
            </Flex>
          )}
        </Field>
        <ButtonLabelFields input={input} meta={meta} />
      </Flex>
    </Flex>
  );
};

const ButtonLabelFields: FC<FieldRenderProps<IFormBlockSubmit>> = ({ input, meta }) => {
  const areLabelsEqualToDefault =
    input.value.sendSuccessLabel === SUBMIT_SUCCESS_MESSAGE && input.value.sendErrorLabel === SUBMIT_ERROR_MESSAGE;

  const [areLabelsCustom, setAreLabelsCustom] = useState(!areLabelsEqualToDefault);
  const [rememberedSuccessLabel, setRememberedSuccessLabel] = useState<string>(
    input.value.sendSuccessLabel || SUBMIT_SUCCESS_MESSAGE
  );
  const [rememberedErrorLabel, setRememberedErrorLabel] = useState<string>(
    input.value.sendErrorLabel || SUBMIT_ERROR_MESSAGE
  );

  const handleToggleCustomLabels = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.checked;
    if (!value) {
      setAreLabelsCustom(false);
      input.onChange({
        ...input.value,
        sendSuccessLabel: SUBMIT_SUCCESS_MESSAGE,
        sendErrorLabel: SUBMIT_ERROR_MESSAGE,
      });
    } else {
      setAreLabelsCustom(true);
      input.onChange({
        ...input.value,
        sendSuccessLabel: rememberedSuccessLabel,
        sendErrorLabel: rememberedErrorLabel,
      });
    }
  };

  const handleChangeSuccessLabel = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRememberedSuccessLabel(event.target.value);
    input.onChange({ ...input.value, sendSuccessLabel: event.target.value });
  };

  const handleChangeErrorLabel = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRememberedErrorLabel(event.target.value);
    input.onChange({ ...input.value, sendErrorLabel: event.target.value });
  };

  return (
    <>
      <Flex marginV={withVar(SpacingToken.Spacing2)} gap={withVar(SpacingToken.Spacing2)} alignItems="center">
        <Switch
          size="medium"
          on={areLabelsCustom}
          onChange={handleToggleCustomLabels}
          data-testid="custom-labels-toggle"
        />
        <Text>Custom notification messages</Text>
      </Flex>
      {areLabelsCustom && (
        <>
          <ErrorFormField
            css={{ marginBottom: withVar(SpacingToken.Spacing2) }}
            labelText="Button content on success"
            error={meta.error?.sendSuccessLabel}
          >
            <Input value={rememberedSuccessLabel} onChange={handleChangeSuccessLabel} data-testid="success-label" />
          </ErrorFormField>
          <ErrorFormField labelText="Button content on error" error={meta.error?.sendErrorLabel}>
            <Input value={rememberedErrorLabel} onChange={handleChangeErrorLabel} data-testid="error-label" />
          </ErrorFormField>
        </>
      )}
    </>
  );
};

const ErrorFormField = styled(FormField)`
  margin-bottom: ${withVar(SpacingToken.Spacing2)};
`;
